import {useHistory, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import {ListSearch} from '../../common/ListSearch';
import {ListPagination} from '../../common/ListPagination';
import NoItems from '../../common/no_items/NoItems';
import Container from '../../common/layout/Container';
import Error from '../../common/Error';
import {getSortedParams} from '../../../helpers';
import Spinner from '../../common/Spinner';
import {useCallback, useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {setTitle} from '../../../store/header/header.slice';
import TaskListRow from './TaskListRow';
import permissionsInfo from '../../../constants/permissionsInfo';
import url from 'url';
import {getAllTasks} from '../../../api/tasks';
import {TaskListResponseProps} from '../../../constants/types';
import EnsurePermission from '../../common/EnsurePermission';
import ConfirmationClaimPopup from '../ConfirmationClaimPopup';
import {SuccessClaimCallbackData, TaskToClaim} from '../types';
import {shouldShowClaimButton} from '../helpers';
import routes from '../routes';
import TableHeaderRow from '../../common/TableHeaderRow';
import {noItemsMessages} from '../../../constants/constants';
import classNames from 'classnames';

const initialQuery = {
  direction: 'desc',
  page: '1',
  per_page: '10',
  simple_search: '',
  sort: '',
  quick_filter: 'GROUP'
};

export const quickFilterParams = {
  all: 'ALL',
  group: 'GROUP',
  my: 'MY'
};

const entity = {
  fetchUrl: () => '/tasks',
  header: 'List of Tasks',
  title: '',
  permissions: permissionsInfo.tasks.view,
  loadingText: 'Getting tasks list',
  defaultSort: 'STARTED_AT',
  fields: [{
    name: 'taskName',
    label: 'Task Name',
    permission: null
  },
  {
    name: 'processName',
    label: 'Process Name',
    permission: null
  },
  {
    name: 'groups',
    label: 'Groups',
    permission: null
  },
  {
    name: 'startedAt',
    label: 'Created',
    sortName: 'STARTED_AT',
    permission: null
  },
  {
    name: 'taskTimer',
    label: 'Task Timer',
    permission: null
  },
  {
    name: 'operatoryNumber',
    label: 'Operatory',
    permission: null
  },
  {
    name: 'teamMember',
    label: 'Team Member',
    permission: null
  },
  {
    name: 'claim',
    label: '',
    className: 'narrow-cell',
    permission: permissionsInfo.tasks.claim
  }
  ]
};

export const TaskList = () => {
  const dispatch = useAppDispatch();
  const {pathname, search} = useLocation();
  const history = useHistory();
  const {activeTenant} = useAppSelector(({permissions}) => permissions);
  const {permissions: currentUserPermissions} = useAppSelector((state) =>
    state.permissions);
  const {
    groups: userGroups = [],
    id: userId = null
  } = useAppSelector((state) => state.user);

  const query = {
    ...initialQuery,
    ...getSortedParams(queryString.parse(search))
  };

  const allTasksQuery = {
    ...initialQuery, quick_filter: quickFilterParams.all, per_page: '1'
  };

  const groupTasksQuery = {
    ...initialQuery, quick_filter: quickFilterParams.group, per_page: '1'
  };
  const myTasksQuery = {
    ...initialQuery, quick_filter: quickFilterParams.my, per_page: '1'
  };

  if (!query.sort) {
    query.sort = entity?.defaultSort;
  }

  const quickFilter = query.quick_filter || 'GROUP';
  const [error, setError] = useState('');
  const [taskListData, setTaskListData] =
    useState<Nullable<TaskListResponseProps>>(null);
  const [allTasksCount, setAllTasksCount] = useState<number | ''>('');
  const [groupTasksCount, setCountGroupTasks] = useState<number | ''>('');
  const [myTasksCount, setMyTasksCount] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isGettingCount, setIsGettingCount] = useState(true);
  const [taskToClaim, setTaskToClaim] = useState<Nullable<TaskToClaim>>(null);

  useEffect(() => {
    history.replace(url.format({pathname, query}));
    dispatch(setTitle('Tasks'));
  }, []);

  const getTaskListData = async () => {
    setIsLoading(true);
    try {
      const response = await getAllTasks(query);
      setTaskListData(response);
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if ((query.page !== '1' || query.simple_search) && taskListData) {
      history.push(url.format({
        pathname, query: {...query, page: 1, simple_search: ''}}));
    }
  }, [activeTenant]);

  useEffect(() => {
    getTaskListData();

    return () => {
      setError('');
    };
  }, [JSON.stringify(query)]);

  const {content = [], pageNumber = 1, totalElements = 0, totalPages = 1} =
  taskListData || {};
  const contentLength = content?.length;

  const getCount = async () => {
    setIsGettingCount(true);

    const canGetAll = permissionsInfo.tasks.view.some((permission) =>
      currentUserPermissions.includes(permission));

    const canGetGroup = permissionsInfo.tasks.group.view.some((permission) =>
      currentUserPermissions.includes(permission));

    const canGetMy = permissionsInfo.tasks.personal.view.some((permission) =>
      currentUserPermissions.includes(permission));

    try {
      const getAllTasksCount = () =>
        canGetAll ? getAllTasks(allTasksQuery) : null;
      const getGroupTasksCount = () =>
        canGetGroup ? getAllTasks(groupTasksQuery) : null;
      const getMyTasksCount = () =>
        canGetMy ? getAllTasks(myTasksQuery) : null;

      const [AllTasksResponse, GroupTasksResponse, MyTasksResponse] =
        await Promise.all([
          getAllTasksCount(),
          getGroupTasksCount(),
          getMyTasksCount()
        ]);

      if (canGetAll) {
        setAllTasksCount(AllTasksResponse?.totalElements);
      }
      if (canGetGroup) {
        setCountGroupTasks(GroupTasksResponse?.totalElements);
      }
      if (canGetMy) {
        setMyTasksCount(MyTasksResponse?.totalElements);
      }
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setIsGettingCount(false);
    }
  };

  useEffect(() => {
    getCount();
  }, []);

  const handleOpenClaimPopup = useCallback((taskDetails: TaskToClaim) => {
    setTaskToClaim(taskDetails);
  }, []);

  const handleCloseClaimPopup = useCallback(() => {
    setTaskToClaim(null);
  }, []);

  const successClaimCallback = useCallback(({
    processId
  }: SuccessClaimCallbackData) => {
    history.push(routes.item(processId));
  }, []);

  if (error) {
    return <Error message={error} />;
  }

  const noItems = !totalElements && !query?.simple_search && !isLoading;
  const noSearchResults = !totalElements &&
    !!query?.simple_search &&
    !isLoading;

  const renderRows = () => {
    if (!content) return null;

    return content.map(({
      id, type, process, startedAt,
      targetTime, operatoryNumber, groups, assignee
    }) => {
      const groupNames = groups?.map(({name}) => name)?.join(', ');

      const rowItem = {
        taskName: type?.description,
        processName: process.type,
        processId: process.id,
        groups: groupNames,
        startedAt: startedAt,
        targetTime: targetTime,
        teamMember:
          assignee ? `${assignee.lastName}, ${assignee.firstName}` : '',
        operatoryNumber: operatoryNumber
      };

      const shouldShowButton = shouldShowClaimButton({
        taskGroups: groups, userGroups,
        userId, taskAssignee: assignee, isRunning: true
      });

      return (
        <TaskListRow
          key={`tasklist-row-${id}`}
          id={id}
          rowItem={rowItem}
          handleOpenClaimPopup={handleOpenClaimPopup}
          shouldShowClaimButton={shouldShowButton}
        />
      );
    });
  };

  const tableContent = isLoading && !taskListData ?
    (<Spinner loadingText={entity?.loadingText}/>) :
    (<Spinner loadingText={entity?.loadingText} isLoading={isLoading}>
      <>
        <table className="table tasklist-wrapper">
          <thead>
            <TableHeaderRow
              query={query}
              tableData={entity.fields}
            />
          </thead>
          <tbody>
            {renderRows()}
          </tbody>
        </table>

        {noSearchResults ?
          (<NoItems message={noItemsMessages.SEARCH}/>) :
         noItems ?
           (<NoItems message={noItemsMessages.ITEMS}/>) :
           (<ListPagination
             contentLength={contentLength}
             pageNumber={pageNumber}
             totalElements={totalElements}
             totalPages={totalPages}
             query={query}
           />)
        }
      </>
    </Spinner>);

  return (
    <>
      <Container header={entity?.header}>
        <div className="tasklist">
          <div className="quick-filters">
            <div className="quick-filters-wrapper">
              <span>Quick Filters:</span>
              <EnsurePermission
                elementPermissions={permissionsInfo.tasks.view}>
                <div
                  className={classNames('quick-filters_element',
                      {'active': quickFilter === quickFilterParams.all})}
                  onClick={() => {
                    history.push(
                        url.format({
                          pathname,
                          query: {
                            ...query,
                            quick_filter: quickFilterParams.all
                          }
                        }));
                  }}>
                  <>
                    {'All tasks '} {isGettingCount ? <Spinner embedded /> :
                    `(${allTasksCount})`}
                  </>
                </div>
              </EnsurePermission>

              <EnsurePermission
                elementPermissions={permissionsInfo.tasks.group.view}>
                <div
                  className={classNames('quick-filters_element',
                      {'active': quickFilter === quickFilterParams.group})}
                  onClick={() => {
                    history.push(
                        url.format({
                          pathname,
                          query: {
                            ...query,
                            quick_filter: quickFilterParams.group
                          }
                        }));
                  }}>
                  <>
                    {'My Group Tasks '} {isGettingCount ? <Spinner embedded /> :
                    `(${groupTasksCount})`}
                  </>
                </div>
              </EnsurePermission>

              <EnsurePermission
                elementPermissions={permissionsInfo.tasks.personal.view}>
                <div
                  className={classNames('quick-filters_element',
                      {'active': quickFilter === quickFilterParams.my})}
                  onClick={() => {
                    history.push(
                        url.format({
                          pathname,
                          query: {
                            ...query,
                            quick_filter: quickFilterParams.my
                          }
                        }));
                  }}>
                  <>
                    {'My tasks '} {isGettingCount ? <Spinner embedded /> :
                      `(${myTasksCount})`}
                  </>
                </div>
              </EnsurePermission>
            </div>
            <ListSearch
              entity={entity}
              query={query}
            />
          </div>
          {tableContent}
        </div>
      </Container>

      <ConfirmationClaimPopup
        task={taskToClaim}
        closePopup={handleCloseClaimPopup}
        successClaimCallback={successClaimCallback}
      />
    </>
  );
};

export default TaskList;
